<script setup lang="ts">
import {ref} from "vue";
import {useRoute} from "vue-router";
import {ElMessageBox, ElNotification} from "element-plus";
import {CourseService, SectionService} from "@/api/api.ts";
import {AddRecordSection, CourseInfo, SaveCourse, SaveLiveSection, SaveRecordSection} from "@/types";
import CourseDetailHeader from "components/course/detail/CourseDetailHeader.vue";
import StudentListDialog from "components/dialog/apply/StudentListDialog.vue";
import AddEditNoticeDialog from "components/dialog/notice/AddEditNoticeDialog.vue";
import PreviewDialog from "components/dialog/PreviewDialog.vue";
// 直播录播相关
import CourseNotice from "components/course/detail/CourseNotice.vue";
import CourseTimeTable from "components/course/detail/CourseTimeTable.vue";
// 录播课章节相关
import AddRecordSectionDialog from "components/dialog/record/AddRecordSectionDialog.vue";
import EditRecordSectionDialog from "components/dialog/record/EditRecordSectionDialog.vue";
import UploadRecordVideoDialog from "components/dialog/record/UploadRecordVideoDialog.vue";
// 直播课章节相关
import AddLiveSectionDialog from "components/dialog/live/AddLiveSectionDialog.vue";
import EditLiveSectionDialog from "components/dialog/live/EditLiveSectionDialog.vue";
// 系列课组合课相关
import AddedCourseList from "components/course/detail/AddedCourseList.vue";
import SeriesAddCourseDialog from "components/dialog/series/SeriesAddCourseDialog.vue";
import GroupAddCourseDialog from "components/dialog/group/GroupAddCourseDialog.vue";
import CourseEditDialog from "components/dialog/CourseEditDialog.vue";
import router from "@/router";
import SectionStudyDialog from "components/dialog/SectionStudyDialog.vue";
import SecretInfoDialog from "components/dialog/SecretInfoDialog.vue";
import LiveVideoPlayDialog from "components/dialog/live/LiveVideoPlayDialog.vue";
import BatchAddSectionDialog from "components/dialog/common/innner/BatchAddSectionDialog.vue";
import RecordVideoPlayDialog from "components/dialog/record/RecordVideoPlayDialog.vue";

// 页面数据加载中
const loading = ref(false);
const route = useRoute();
const params = route.params;

// 课程基本信息编辑
const courseEditDialog = ref<InstanceType<typeof CourseEditDialog>>();
const addEditNoticeDialog = ref<InstanceType<typeof AddEditNoticeDialog>>();
const previewDialog = ref<InstanceType<typeof PreviewDialog>>();
const studentListDialog = ref<InstanceType<typeof StudentListDialog>>();
const secretInfoDialog = ref<InstanceType<typeof SecretInfoDialog>>();

const sectionStudyDialog = ref<InstanceType<typeof SectionStudyDialog>>();

// **** 直播课对话框的引用 ****/
// 添加直播课章节对话框的引用
const addLiveSectionDialog = ref<InstanceType<typeof AddLiveSectionDialog>>();
// 添加直播课章节对话框的引用
const editLiveSectionDialog = ref<InstanceType<typeof EditLiveSectionDialog>>();
const liveVideoPlayDialog = ref<InstanceType<typeof LiveVideoPlayDialog>>();

// **** 录播课对话框的引用 ****/
// 添加录播课章节对话框的引用
const addRecordSectionDialog = ref<InstanceType<typeof AddRecordSectionDialog>>();
// 修改录播课章节对话框的引用
const editRecordSectionDialog = ref<InstanceType<typeof EditRecordSectionDialog>>();
// 上传录播课章节对话框的引用
const uploadRecordVideoDialog = ref<InstanceType<typeof UploadRecordVideoDialog>>();
// 录播课批量添加章节对话框的引用
const batchAddSectionDialog = ref<InstanceType<typeof BatchAddSectionDialog>>();
const recordVideoPlayDialog = ref<InstanceType<typeof RecordVideoPlayDialog>>();

// **** 系列课组合课相关的引用 ****/
// 添加系列课对话框的引用
const seriesAddCourseDialog = ref<InstanceType<typeof SeriesAddCourseDialog>>();
// 添加组合课对话框的引用
const groupAddCourseDialog = ref<InstanceType<typeof GroupAddCourseDialog>>();

// 课程基本信息
const courseInfo = ref<CourseInfo>();

// 获取课程基本信息
const getCourseInfo = async () => {
  try {
    loading.value = true;
    const res = await CourseService.getCourseInfo({
      id: parseInt(params.id as string)
    })
    if (res.data.code === 200) {
      courseInfo.value = res.data.data;
    } else {
      ElNotification.error({
        title: '获取课程详情失败',
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (error) {
    console.log(error)
  } finally {
    loading.value = false;
  }
}

// 确认发布
const onCourseSubmit = async (info: any) => {
  try {
    const res = await CourseService.courseIssue(info)
    if (res.data.code === 200) {
      ElNotification.success({
        message: res.data.msg,
      })
      previewDialog.value?.hide()
      await getCourseInfo()
    } else {
      ElNotification.error({
        title: "发布失败",
        message: res.data.msg,
      })
    }
  } catch (e) {
    console.warn(e)
  }
}

const onEditCourse = async (info: SaveCourse) => {
  try {
    info.id = parseInt(params.id as string);
    // @ts-ignore
    const res = await CourseService.saveCourse(info)
    if (res.data.code === 200) {
      ElNotification.success({
        title: "成功",
        message: res.data.msg,
      })
      courseEditDialog.value?.hide()
      await getCourseInfo()
    } else {
      ElNotification.error({
        title: "成功",
        message: res.data.msg,
      })
    }
  } catch (e) {
    console.warn(e)
  }
}

// 确认更改或发布公告成功
const onAddEditNoticeSuccess = () => {
  getCourseInfo()
}

// 批量增加章节成功
const onBatchAddSectionSuccess = () => {
  getCourseInfo()
  batchAddSectionDialog.value?.hide()
}

// 获取私密课程信息
const getSecretInfo = async () => {
  try {
    const res = await CourseService.getSecretShareInfo({
      id: parseInt(params.id as string)
    })
    if (res.data.code === 200) {
      const shareInfo = res.data.data
      if (shareInfo) {
        secretInfoDialog.value?.showDialog(shareInfo)
      } else {
        ElNotification.error({
          title: "获取私密课程信息失败",
          message: "信息为空",
          type: 'error',
        })
      }
    } else {
      ElNotification.error({
        title: "获取私密课程信息失败",
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (error) {
    console.log(error)
  }
}

// **** 系列课组合课相关 ****//
// 系列课/组合课添加课程对话框
const onAddCourse = () => {
  if (courseInfo.value?.info?.type === 3) {
    seriesAddCourseDialog.value?.init(
        1,
        parseInt(params.id as string)
    )
  } else if (courseInfo.value?.info?.type === 4) {
    groupAddCourseDialog.value?.init(
        2,
        parseInt(params.id as string)
    )
  }
}

// 系列课/组合课删除包含的课程
const onDeleteCourse = (id: number) => {
  if (courseInfo.value?.info?.type === 3) {
    addRemoveSeriesCourse(id.toString(), 2)
  } else if (courseInfo.value?.info?.type === 4) {
    addRemoveGroupCourse(id, 2)
  }
}

// 课程详情
const onDetail = (id: number) => {
  const routeData = router.resolve({
    name: 'CourseDetail',
    params: {
      id: id,
    }
  })
  window.open(routeData.href, '_blank')
}

// 系列课确认添加
const onAddSeriesCourseSure = async (ids: string) => {
  await addRemoveSeriesCourse(ids, 1)
}

// 系列课添加删除课程
const addRemoveSeriesCourse = async (ids: string, type: number) => {
  try {
    const res = await CourseService.addDelSeriesCourse({
      id: parseInt(params.id as string),
      course_id: ids,
      type: type,
    })
    if (res.data.code === 200) {
      ElNotification.success({
        title: '成功',
        message: res.data.msg,
        type: 'success',
      })
      await getCourseInfo()
      seriesAddCourseDialog.value?.hide()
    } else {
      ElNotification.error({
        title: '失败',
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (error) {
    console.log(error)
  }
}

// 组合课确认添加
const onAddGroupCourseSure = (id: number) => {
  addRemoveGroupCourse(id, 1)
}

// 组合课确认删除
const onRemoveGroupCourseSure = (id: number) => {
  addRemoveGroupCourse(id, 2)
}

// 组合课添加/删除课程
const addRemoveGroupCourse = async (id: number, way: number = 1) => {
  try {
    const res = await CourseService.addDelGroupCourse({
      id: parseInt(params.id as string),
      way: way,
      group_id: id,
    })
    if (res.data.code === 200) {
      ElNotification.success({
        title: '成功',
        message: res.data.msg,
        type: 'success',
      })
      await getCourseInfo()
      groupAddCourseDialog.value?.hide()
    } else {
      ElNotification.error({
        title: '失败',
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (error) {
    console.log(error)
  }
}

// **** 直播录播公共功能 ****//

// 新增章节对话框
const addSection = () => {
  if (courseInfo.value?.info?.type === 2) {
    // 录播课新增章节
    addRecordSectionDialog.value?.show()
  } else if (courseInfo.value?.info?.type === 1) {
    // 直播课新增章节
    addLiveSectionDialog.value?.show(courseInfo.value.info.is_fake === 1)
  }
}

// 编辑章节信息对话框
const onEditSection = async (id: number) => {
  if (courseInfo.value?.info?.type === 2) {
    // 录播课编辑章节
    editRecordSectionDialog.value?.show(parseInt(params.id as string), id)
  } else if (courseInfo.value?.info?.type === 1) {
    // 直播课编辑章节
    editLiveSectionDialog.value?.show(parseInt(params.id as string), id, courseInfo.value?.info.is_fake === 1)
  }
}

const getStudyList = (_id: number, _course_type: number) => {
  sectionStudyDialog.value?.show(_id, _course_type)
}

const onStudyListClose = () => {
  sectionStudyDialog.value?.hide()
}

// **** 直播课相关的功能 ****//
// 直播课新增章节确认
const onAddLiveSection = async (info: AddRecordSection) => {
  info.id = parseInt(params.id as string);
  try {
    //@ts-ignore 取消对id类型不兼容的提示
    const res = await SectionService.addLiveSection(info)
    if (res.data.code === 200) {
      ElNotification.success({
        title: "新增成功",
        message: res.data.msg,
        type: 'success',
      })
      await getCourseInfo()
      addLiveSectionDialog.value?.hide()
    } else {
      ElNotification.error({
        title: "新增失败",
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (error) {
    console.warn(error)
  }
}

// 直播课编辑章节确认
const onEditLiveSection = async (info: SaveLiveSection) => {
  info.course_id = parseInt(params.id as string);
  try {
    //@ts-ignore 取消对id类型不兼容的提示
    const res = await SectionService.saveLiveSection(info)
    if (res.data.code === 200) {
      ElNotification.success({
        title: "编辑成功",
        message: res.data.msg,
        type: 'success',
      })
      await getCourseInfo()
      editLiveSectionDialog.value?.hide()
    } else {
      ElNotification.error({
        title: "编辑失败",
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (error) {
    console.warn(error)
  }
}

// 开始直播
const onStartLive = (id: number) => {
  router.push({
    name: "FakeLiveDetail",
    params: { id: id }
  })
}

// **** 录播课相关的功能 ****//

// 录播课新增章节确认
const onAddRecordSection = async (info: AddRecordSection) => {
  info.id = parseInt(params.id as string)
  try {
    //@ts-ignore 取消对id类型不兼容的提示
    const res = await SectionService.addRecordSection(info)
    if (res.data.code === 200) {
      ElNotification.success({
        title: "新增成功",
        message: res.data.msg,
        type: 'success',
      })
      await getCourseInfo()
      addRecordSectionDialog.value?.hide()
    } else {
      ElNotification.error({
        title: "新增失败",
        message: res.data.msg,
        type: 'success',
      })
    }
  } catch (err) {
    console.log(err)
  }
}

// 录播课修改章节基本信息确认
const onSaveRecordSection = async (info: SaveRecordSection) => {
  info.course_id = parseInt(params.id as string)
  try {
    // @ts-ignore 取消对course_id类型不兼容的提示
    const res = await SectionService.saveRecordSection(info)
    if (res.data.code === 200) {
      ElNotification.success({
        title: "编辑成功",
        message: res.data.msg,
        type: 'success',
      })
      await getCourseInfo()
      editRecordSectionDialog.value?.hide()
    } else {
      ElNotification.error({
        title: "编辑失败",
        message: res.data.msg,
        type: 'success',
      })
    }
  } catch (err) {
    console.log(err)
  }
}

// 录播课删除章节信息
const onDeleteSection = async (id: number) => {
  ElMessageBox.confirm(
      '确认删除该章节吗',
      '注意',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
  )
      .then(() => {
        return SectionService.delCourseSection({
          course_id: parseInt(params.id as string),
          section_id: id,
        })
      })
      .then(res => {
        if (res.data.code === 200) {
          ElNotification.success({
            title: '删除成功',
            message: res.data.msg,
            type: 'success',
          })
          getCourseInfo()
        } else {
          ElNotification.error({
            title: '删除章节失败',
            message: res.data.msg,
            type: 'error',
          })
        }
      })
      .catch(() => {
      })
}

const upDownSection = async (status: number, id: number) => {
  try {
    await ElMessageBox.confirm(
        status === 1 ? '确认上架该章节吗' : `确认下架该章节吗`,
        '注意',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
    )
    const res = await SectionService.upDownSection({
      course_id: parseInt(params.id as string),
      section_id: id,
      status: status,
    })
    if (res.data.code === 200) {
      ElNotification.success({
        title: res.data.msg,
      })
      await getCourseInfo()
    } else {
      ElNotification.error({
        message: res.data.msg,
      })
    }
  } catch (error) {
    console.warn(error)
  }
}

// 录播课新增章节时选择已上传视频回调
const onChangeVideo = async (id: number, video_id: string) => {
  uploadRecordVideoDialog.value?.show(video_id, id)
}

// 录播课单独上传或者修改章节视频
const onChangeRecordSectionVideo = async (sectionId: number, video_id: string) => {
  try {
    const res = await SectionService.saveRecordSection({
      course_id: parseInt(params.id as string),
      section_id: sectionId,
      video_id: video_id,
    })
    if (res.data.code === 200) {
      uploadRecordVideoDialog.value?.hide()
      ElNotification.success({
        title: "成功",
        message: res.data.msg,
        type: "success",
      })
      await getCourseInfo()
    } else {
      ElNotification.error({
        title: "失败",
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (err) {
    console.warn(err)
  }
}

const onEditInfo = () => {
  console.log("...edit..")
  courseEditDialog.value?.show(parseInt(params.id as string))
}

const onPublishNotice = (_id: number) => {
  addEditNoticeDialog.value?.show(parseInt(params.id as string), _id)
}

const onDeleteNotice = (_id: number) => {
  deleteNotice(_id)
}

const deleteNotice = async (id: number) => {
  try {
    await ElMessageBox.confirm(
        '确认删除该公告吗',
        '注意',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
    )
    const res = await CourseService.delNotice({
      course_id: parseInt(params.id as string),
      notice_id: id,
    })
    if (res.data.code === 200) {
      ElNotification.success({
        title: "成功",
        message: res.data.msg,
      })
      await getCourseInfo()
    } else {
      ElNotification.error({
        title: "成功",
        message: res.data.msg,
      })
    }
  } catch (e) {
    console.warn(e)
  }
}
const onPreviewPublish = () => {
  previewDialog.value?.show(parseInt(params.id as string))
}

const showStudentList = () => {
  studentListDialog.value?.show(parseInt(params.id as string))
}

const onBatchAddSection = () => {
  console.log("show")
  batchAddSectionDialog.value?.show(parseInt(params.id as string))
}

const handleMyEvent = (videoId: string) => {
    console.log(videoId);
}

const onVideoClick = (videoId: string,type:string) => {
  recordVideoPlayDialog.value?.show(videoId,type)
}

// 调用获取课程信息
getCourseInfo()
</script>

<template>
  <div class="px-3rem pt-2.25rem min-h-50%" v-loading="loading">
    <div class="flex items-center mb-1.5rem gap-1.25rem">
      <div class="w-.3125rem h-1.5rem bg-#ff6600cc rounded-md"></div>
      <div class="text-[rgba(0,0,0,0.85)] text-1.25em">
        {{ courseInfo?.info?.is_secret === 1 ? "私密课详情" : "课程详情" }}
      </div>
    </div>
    <!-- 详情区域  -->
    <course-detail-header
        :info="courseInfo?.info"
        @on-edit-info="onEditInfo"
        @on-publish-notice="onPublishNotice"
        @preview-publish="onPreviewPublish"
        @show-student-list="showStudentList"
        @get-secret-info="getSecretInfo">
    </course-detail-header>
    <!-- 公告区域  -->
    <course-notice
        class="mt-1.5rem"
        @on-edit="onPublishNotice"
        @on-delete="onDeleteNotice"
        v-if="courseInfo && (courseInfo.info.type === 1 || courseInfo.info.type === 2)"
        :notice-list="courseInfo?.notice || []">
    </course-notice>
    <!-- 课程表 -->
    <course-time-table
        class="mt-1.5rem"
        v-if="(courseInfo?.info?.type === 1 || courseInfo?.info?.type === 2) && courseInfo"
        :course_status="courseInfo?.info.status"
        :course_type="courseInfo?.info.type"
        :sections="courseInfo?.section"
        :is_fake="courseInfo?.info.is_fake"
        @add-section="addSection"
        @delete-section="onDeleteSection"
        @on-up-down="upDownSection"
        @start-live="onStartLive"
        @edit-section="onEditSection"
        @study-list="getStudyList"
        @on-change-video="onChangeVideo"
        @batch-add-section="onBatchAddSection"
        @my-event="handleMyEvent"
        @on-video-click="onVideoClick">
    </course-time-table>
    <!-- 组合课系列课  -->
    <added-course-list
        class="mt-1.5rem"
        v-if="(courseInfo?.info?.type === 3 || courseInfo?.info?.type === 4) && courseInfo"
        :courses="courseInfo?.section"
        :status="courseInfo?.info.status"
        @add-course="onAddCourse"
        @del="onDeleteCourse"
        @detail="onDetail"/>
  </div>
  <student-list-dialog ref="studentListDialog" />
  <section-study-dialog ref="sectionStudyDialog" @close="onStudyListClose" />
  <secret-info-dialog ref="secretInfoDialog" />
  <!-- 课程基本信息编辑 -->
  <course-edit-dialog ref="courseEditDialog" @on-edit-course="onEditCourse" />
  <!-- 发布公告 -->
  <add-edit-notice-dialog ref="addEditNoticeDialog" @on-submit-success="onAddEditNoticeSuccess" />
  <!-- 直播课新增章节 -->
  <add-live-section-dialog ref="addLiveSectionDialog" @on-submit="onAddLiveSection"/>
  <edit-live-section-dialog ref="editLiveSectionDialog" @on-submit="onEditLiveSection"/>
  <live-video-play-dialog ref="liveVideoPlayDialog" />
  <!-- 录播课新增/编辑章节 -->
  <add-record-section-dialog ref="addRecordSectionDialog" @on-submit="onAddRecordSection" @on-preview-video="onVideoClick"/>
  <edit-record-section-dialog ref="editRecordSectionDialog" @on-submit="onSaveRecordSection"/>
  <batch-add-section-dialog ref="batchAddSectionDialog" @on-add-success="onBatchAddSectionSuccess"/>
  <record-video-play-dialog ref="recordVideoPlayDialog" />
  <!-- 录播课修改视频 -->
  <upload-record-video-dialog ref="uploadRecordVideoDialog" @on-submit="onChangeRecordSectionVideo" @on-preview-video="onVideoClick"/>
  <!-- 系列课新增课程 -->
  <series-add-course-dialog ref="seriesAddCourseDialog" @add-course="onAddSeriesCourseSure"/>
  <!-- 系列课新增课程 -->
  <group-add-course-dialog
      ref="groupAddCourseDialog"
      @on-add-course="onAddGroupCourseSure"
      @on-remove-course="onRemoveGroupCourseSure"/>
  <!-- 课程预览 -->
  <preview-dialog ref="previewDialog" @submit="onCourseSubmit" />
</template>

<style scoped>
</style>
